import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { MonogramMark } from "../components/icons";
import { formatDate } from "../components/Blogs";

const emptyDraft = { title: "", tag: "GENERAL", excerpt: "", content: "", cover: "", published: true };

const inputCls =
  "w-full bg-white border border-[#0D3B3E]/15 px-4 py-3 text-sm text-[#0D3B3E] focus:outline-none focus:border-[#0D9488] transition-colors duration-300";
const labelCls = "block text-[10px] font-mono tracking-[0.2em] text-[#0D9488] mb-2";

const AdminPage = () => {
  const [user, setUser] = useState(undefined);
  const [posts, setPosts] = useState([]);
  const [mode, setMode] = useState("list");
  const [draft, setDraft] = useState(emptyDraft);
  const [editId, setEditId] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPosts = () => api.get("/blogs/admin/all").then((r) => setPosts(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/auth/me").then((r) => { setUser(r.data); loadPosts(); }).catch(() => setUser(null));
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      loadPosts();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const startCreate = () => {
    setDraft(emptyDraft);
    setEditId(null);
    setMode("edit");
    setNotice("");
  };

  const startEdit = (p) => {
    setDraft({ title: p.title, tag: p.tag, excerpt: p.excerpt || "", content: p.content, cover: p.cover || "", published: p.published });
    setEditId(p.id);
    setMode("edit");
    setNotice("");
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...draft, cover: draft.cover || null };
      if (editId) await api.put(`/blogs/${editId}`, payload);
      else await api.post("/blogs", payload);
      setNotice(editId ? "Article updated." : "Article published to the journal.");
      setMode("list");
      loadPosts();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
    try {
      await api.delete(`/blogs/${p.id}`);
      setNotice("Article deleted.");
      loadPosts();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (user === undefined) {
    return <div data-testid="admin-loading" className="min-h-screen bg-[#F8FAF8] flex items-center justify-center text-[10px] font-mono tracking-[0.25em] text-[#0D9488]">LOADING…</div>;
  }

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#F8FAF8] flex items-center justify-center px-5">
        <form data-testid="admin-login-form" onSubmit={doLogin} className="w-full max-w-sm bg-white border border-[#0D3B3E]/10 p-8 sm:p-10">
          <MonogramMark className="w-10 h-10 mb-6" />
          <h1 className="font-display font-semibold text-2xl text-[#0D3B3E] tracking-tight">Journal Admin</h1>
          <p className="text-[10px] font-mono tracking-[0.2em] text-[#0D9488] mt-2 mb-8">DR. MUGDHA ADHIKARI · PRIVATE</p>
          <label className={labelCls} htmlFor="admin-email">EMAIL</label>
          <input id="admin-email" data-testid="admin-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-5`} autoComplete="username" />
          <label className={labelCls} htmlFor="admin-password">PASSWORD</label>
          <input id="admin-password" data-testid="admin-password-input" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mb-6`} autoComplete="current-password" />
          {error && <p data-testid="admin-login-error" className="text-xs text-red-600 mb-4">{error}</p>}
          <button data-testid="admin-login-submit" disabled={busy}
            className="w-full bg-[#0D3B3E] text-[#F8FAF8] text-xs font-mono tracking-[0.18em] py-3.5 hover:bg-[#0D9488] transition-colors duration-300 disabled:opacity-50">
            {busy ? "SIGNING IN…" : "SIGN IN"}
          </button>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.18em] text-slate-500 hover:text-[#0D9488] transition-colors duration-300">
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO SITE
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAF8]">
      <header className="border-b border-[#0D3B3E]/10 bg-[#F8FAF8]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-[64px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MonogramMark className="w-8 h-8" />
            <span className="text-[10px] font-mono tracking-[0.2em] text-[#0D9488]">JOURNAL ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site" className="hidden sm:inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.18em] text-slate-500 hover:text-[#0D9488] transition-colors duration-300">
              <ArrowLeft className="w-3.5 h-3.5" /> SITE
            </Link>
            <button data-testid="admin-logout-btn" onClick={doLogout}
              className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.18em] text-[#0D3B3E] border border-[#0D3B3E]/20 rounded-full px-4 py-2 hover:border-red-400 hover:text-red-500 transition-colors duration-300">
              <LogOut className="w-3.5 h-3.5" /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        {notice && <p data-testid="admin-notice" className="mb-6 text-xs font-mono tracking-[0.12em] text-[#0D9488] border border-[#0D9488]/40 bg-[#0D9488]/[0.06] px-4 py-3">{notice}</p>}
        {error && mode === "list" && <p data-testid="admin-error" className="mb-6 text-xs text-red-600">{error}</p>}

        {mode === "list" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display font-semibold text-2xl sm:text-3xl text-[#0D3B3E] tracking-tight">Your Articles</h1>
              <button data-testid="admin-new-post-btn" onClick={startCreate}
                className="inline-flex items-center gap-2 bg-[#0D3B3E] text-[#F8FAF8] text-[11px] font-mono tracking-[0.18em] rounded-full px-5 py-3 hover:bg-[#0D9488] transition-colors duration-300">
                <Plus className="w-4 h-4" /> NEW POST
              </button>
            </div>
            {posts.length === 0 && (
              <p data-testid="admin-no-posts" className="text-sm text-slate-600 border border-dashed border-[#0D9488]/50 bg-white p-10 text-center">
                No articles yet — write the first one.
              </p>
            )}
            <div className="space-y-3">
              {posts.map((p, i) => (
                <div key={p.id} data-testid={`admin-post-row-${i}`}
                  className="bg-white border border-[#0D3B3E]/10 px-5 py-4 flex flex-wrap items-center gap-3 justify-between">
                  <div className="min-w-0">
                    <p className="font-display font-semibold text-lg text-[#0D3B3E] truncate">{p.title}</p>
                    <p className="text-[10px] font-mono tracking-[0.15em] text-slate-500 mt-1">
                      {p.tag} · {formatDate(p.created_at)} ·{" "}
                      <span className={p.published ? "text-[#0D9488]" : "text-amber-600"}>
                        {p.published ? "PUBLISHED" : "DRAFT"}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`admin-edit-btn-${i}`} onClick={() => startEdit(p)}
                      className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.15em] text-[#0D3B3E] border border-[#0D3B3E]/20 rounded-full px-3.5 py-2 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors duration-300">
                      <Pencil className="w-3 h-3" /> EDIT
                    </button>
                    <button data-testid={`admin-delete-btn-${i}`} onClick={() => remove(p)}
                      className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.15em] text-red-500 border border-red-200 rounded-full px-3.5 py-2 hover:bg-red-50 transition-colors duration-300">
                      <Trash2 className="w-3 h-3" /> DELETE
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {mode === "edit" && (
          <form data-testid="admin-editor-form" onSubmit={save} className="bg-white border border-[#0D3B3E]/10 p-6 sm:p-10">
            <h1 className="font-display font-semibold text-2xl sm:text-3xl text-[#0D3B3E] tracking-tight mb-8">
              {editId ? "Edit Article" : "New Article"}
            </h1>
            <label className={labelCls} htmlFor="post-title">TITLE</label>
            <input id="post-title" data-testid="admin-title-input" required value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={`${inputCls} mb-5`}
              placeholder="e.g. Why homoeopathy treats the person, not the disease" />
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label className={labelCls} htmlFor="post-tag">TAG</label>
                <input id="post-tag" data-testid="admin-tag-input" value={draft.tag}
                  onChange={(e) => setDraft({ ...draft, tag: e.target.value.toUpperCase() })} className={inputCls}
                  placeholder="NUTRITION" />
              </div>
              <div>
                <label className={labelCls} htmlFor="post-cover">COVER IMAGE URL (OPTIONAL)</label>
                <input id="post-cover" data-testid="admin-cover-input" value={draft.cover}
                  onChange={(e) => setDraft({ ...draft, cover: e.target.value })} className={inputCls}
                  placeholder="https://…" />
              </div>
            </div>
            <label className={labelCls} htmlFor="post-excerpt">SHORT SUMMARY (OPTIONAL)</label>
            <input id="post-excerpt" data-testid="admin-excerpt-input" value={draft.excerpt}
              onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} className={`${inputCls} mb-5`}
              placeholder="One or two lines shown on the card" />
            <label className={labelCls} htmlFor="post-content">ARTICLE — separate paragraphs with a blank line</label>
            <textarea id="post-content" data-testid="admin-content-textarea" required rows={14} value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })} className={`${inputCls} mb-5 resize-y leading-relaxed`}
              placeholder="Write your article here…" />
            <label className="flex items-center gap-3 mb-8 cursor-pointer select-none">
              <input data-testid="admin-published-checkbox" type="checkbox" checked={draft.published}
                onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                className="w-4 h-4 accent-[#0D9488]" />
              <span className="text-xs font-mono tracking-[0.15em] text-[#0D3B3E]">PUBLISHED — visible on the website</span>
            </label>
            {error && <p data-testid="admin-save-error" className="text-xs text-red-600 mb-4">{error}</p>}
            <div className="flex flex-wrap gap-3">
              <button data-testid="admin-save-btn" disabled={busy}
                className="bg-[#0D3B3E] text-[#F8FAF8] text-xs font-mono tracking-[0.18em] rounded-full px-7 py-3.5 hover:bg-[#0D9488] transition-colors duration-300 disabled:opacity-50">
                {busy ? "SAVING…" : editId ? "SAVE CHANGES" : "PUBLISH"}
              </button>
              <button type="button" data-testid="admin-cancel-btn" onClick={() => setMode("list")}
                className="text-xs font-mono tracking-[0.18em] text-[#0D3B3E] border border-[#0D3B3E]/25 rounded-full px-7 py-3.5 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors duration-300">
                CANCEL
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
